import { config } from '../config';

const VERSION_ENDPOINT = '/version.json';
const STORAGE_KEY = 'colefax_jobcards_app_version';
const DEFAULT_POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes

type UpdateCallback = () => Promise<void> | void;

type InitOptions = {
  pollIntervalMs?: number;
};

export class VersionUpdateManager {
  private updateCallback?: UpdateCallback;
  private pollTimer?: number;
  private initialized = false;
  private prompting = false;
  private options: InitOptions = {};
  private lastPromptedVersion?: string;

  public setUpdateCallback(callback: UpdateCallback) {
    this.updateCallback = callback;
  }

  public init(options?: InitOptions) {
    if (typeof window === 'undefined' || this.initialized) {
      return;
    }

    this.initialized = true;
    this.options = options ?? {};

    this.persistCurrentVersion();
    this.startPolling();
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  public dispose() {
    if (!this.initialized) {
      return;
    }

    if (this.pollTimer) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = undefined;
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.initialized = false;
  }

  public async handleServiceWorkerRefreshPrompt() {
    await this.promptOrAutoUpdate();
  }

  private persistCurrentVersion() {
    try {
      localStorage.setItem(STORAGE_KEY, config.APP_VERSION);
    } catch (error) {
      console.warn('Unable to persist app version', error);
    }
  }

  private startPolling() {
    if (this.pollTimer || typeof window === 'undefined') {
      return;
    }

    const interval = this.options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL;
    this.pollTimer = window.setInterval(() => {
      this.checkRemoteVersion();
    }, interval);

    // Immediate check on startup
    this.checkRemoteVersion();
  }

  private handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.checkRemoteVersion();
    }
  };

  private async checkRemoteVersion() {
    const remoteVersion = await this.fetchRemoteVersion();
    if (!remoteVersion) {
      return;
    }

    const currentVersion = config.APP_VERSION;
    const hasChanged = remoteVersion !== currentVersion;
    const alreadyPrompted = this.lastPromptedVersion === remoteVersion;

    if (hasChanged && !alreadyPrompted) {
      this.lastPromptedVersion = remoteVersion;
      await this.promptOrAutoUpdate(remoteVersion);
    }
  }

  private async fetchRemoteVersion(): Promise<string | null> {
    try {
      const response = await fetch(`${VERSION_ENDPOINT}?ts=${Date.now()}`, {
        cache: 'no-store',
      });

      if (!response.ok) {
        return null;
      }

      const payload = await response.json();
      return payload.version || null;
    } catch (error) {
      console.warn('Failed to fetch remote version', error);
      return null;
    }
  }

  private async promptOrAutoUpdate(remoteVersion?: string) {
    if (this.prompting) {
      return;
    }

    this.prompting = true;
    const message = remoteVersion
      ? `A new version (${remoteVersion}) is available. Reload to update?`
      : 'New content is available. Reload to update?';

    const shouldUpdate = config.AUTO_UPDATE_ON_VERSION_CHANGE
      ? true
      : window.confirm(message);

    if (shouldUpdate) {
      await this.applyUpdate();
    }

    this.prompting = false;
  }

  private async applyUpdate() {
    if (!this.updateCallback) {
      console.warn('No update callback registered for VersionUpdateManager');
      return;
    }

    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        await registration?.update();
      }
    } catch (error) {
      console.warn('Service worker update failed', error);
    }

    await this.updateCallback();
    window.location.reload();
  }
}

export const versionUpdateManager = new VersionUpdateManager();
